/**
 * Cloud Sync Status Indicator
 *
 * Displays the current sync status in the UI
 */

import React from "react";
import type { SyncStatus } from "../data/CloudStorage";

import "./CloudSyncIndicator.scss";

interface CloudSyncIndicatorProps {
    status: SyncStatus;
    onClick?: () => void;
}

export const CloudSyncIndicator: React.FC<CloudSyncIndicatorProps> = ({
    status,
    onClick,
}) => {
    const getStatusIcon = () => {
        switch (status.state) {
            case "syncing":
                return (
                    <svg
                        className="cloud-sync-icon syncing"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                    >
                        <path d="M19 16.9A5 5 0 0 0 18 7h-1.26a8 8 0 1 0-11.62 9" />
                        <polyline points="13 11 9 15 15 15 11 19" />
                    </svg>
                );
            case "success":
                return (
                    <svg
                        className="cloud-sync-icon success"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                    >
                        <path d="M19 16.9A5 5 0 0 0 18 7h-1.26a8 8 0 1 0-11.62 9" />
                        <polyline points="9 11 12 14 22 4" />
                    </svg>
                );
            case "error":
                return (
                    <svg
                        className="cloud-sync-icon error"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                    >
                        <path d="M19 16.9A5 5 0 0 0 18 7h-1.26a8 8 0 1 0-11.62 9" />
                        <line x1="12" y1="10" x2="12" y2="14" />
                        <line x1="12" y1="18" x2="12.01" y2="18" />
                    </svg>
                );
            default:
                return (
                    <svg
                        className="cloud-sync-icon idle"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                    >
                        <path d="M19 16.9A5 5 0 0 0 18 7h-1.26A8 8 0 1 0 5.74 16" />
                    </svg>
                );
        }
    };

    const getStatusText = () => {
        switch (status.state) {
            case "syncing":
                return "Syncing...";
            case "success":
                if (status.lastSyncTime) {
                    const seconds = Math.floor((Date.now() - status.lastSyncTime) / 1000);
                    if (seconds < 60) {
                        return "Synced just now";
                    } else if (seconds < 3600) {
                        const minutes = Math.floor(seconds / 60);
                        return `Synced ${minutes}m ago`;
                    } else {
                        const hours = Math.floor(seconds / 3600);
                        return `Synced ${hours}h ago`;
                    }
                }
                return "Synced";
            case "error":
                return status.errorMessage || "Sync failed";
            default:
                return "Cloud sync";
        }
    };

    return (
        <div
            className={`cloud-sync-indicator cloud-sync-indicator--${status.state}`}
            onClick={onClick}
            title={getStatusText()}
            role={onClick ? "button" : undefined}
            tabIndex={onClick ? 0 : undefined}
        >
            {getStatusIcon()}
            <span className="cloud-sync-text">{getStatusText()}</span>
        </div>
    );
};
